import { useLocation } from "react-router-dom";
import BugReportForm, { DiagnosticLogs } from "@jkast90/kast-common/bug-report-form";
import { storedToken } from "@common/api";
import { useAppDispatch, useAppSelector } from "@common/store/hooks";
import { decodeJwt, redirectToLogin, refreshStoredTokens } from "@common/store/slices/authSlice";
import { useAdminMode } from "@common/adminMode";

/** CloudBoy's id in the kast-dev bug report inbox: its deploy and metadata prefix. */
export const BUG_REPORT_APP = "cloudboy";

const FORM_CLASSES = {
  button: "btn",
  primaryButton: "btn primary",
  input: "input",
  label: "field-label",
  hint: "muted small",
};

/** The stored token, refreshed first when it has expired: the inbox refuses an expired one. */
export async function bugReportToken(): Promise<string | null> {
  const token = storedToken();
  const exp = Number(token ? decodeJwt(token)?.exp : 0);
  if (token && exp * 1000 <= Date.now()) {
    await refreshStoredTokens();
    return storedToken();
  }
  return token;
}

interface Props {
  user: { id: string | null; email: string | null } | null;
  /** The logs are shown beside the form to admins with admin tools on. */
  admin: boolean;
  onSignIn: () => void;
  /** The crash an ErrorBoundary handed over. */
  initialDescription?: string;
}

/**
 * The report form, with no router or store hooks, so the app-level crash
 * screen can show it outside the providers. Reports go to the kast-dev
 * admin inbox, not CloudBoy's API.
 */
export function BugReport({ user, admin, onSignIn, initialDescription = "" }: Props) {
  return (
    <div className="about-main">
      <div className="library-head">
        <h1>Report a bug</h1>
      </div>
      <section className="panel">
        <BugReportForm
          app={BUG_REPORT_APP}
          user={user}
          getToken={bugReportToken}
          onSignIn={onSignIn}
          initialDescription={initialDescription}
          classNames={FORM_CLASSES}
        />
      </section>
      {admin && (
        <section className="panel">
          <h2>Attached logs</h2>
          <DiagnosticLogs classNames={FORM_CLASSES} />
        </section>
      )}
    </div>
  );
}

/** /bug-report: pre-filled when arriving from a crash screen's Report This. */
export default function BugReportPage() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isAuthenticated, userSub, userEmail } = useAppSelector((s) => s.auth);
  const { unlocked } = useAdminMode();
  const crashNote = (location.state as { crashNote?: string } | null)?.crashNote ?? "";
  return (
    <BugReport
      user={isAuthenticated ? { id: userSub, email: userEmail } : null}
      admin={unlocked}
      onSignIn={() => dispatch(redirectToLogin())}
      initialDescription={crashNote}
    />
  );
}
